// StoriesView — the Stories board.
//
// Cloned from PostsView.tsx, scoped to post_type=story. Divergences vs Posts:
//   - trimmed stages via storiesStageConfig (draft/scheduled/published/discarded)
//   - DROPPED the content-type tab strip (Tutti/Post/Reel) — stories have no
//     post/reel split.
//   - DROPPED list/scheduled view modes + StatusStatistics + ShareWithClientModal
//     (all feed-post specific: ListPostCard / ScheduledPostsList render feed
//     posts, StatusStatistics counts the full feed lifecycle, and there is no
//     approval stage in the trimmed story flow). Stories render as a single
//     stage-filtered card grid.
//   - Card: StoriesCard (trimmed EditorPostCard clone — schedule/publish/delete
//     menu + duration guard).
import { SpinnerIcon, ErrorIcon, RefreshIcon, LibraryIcon } from "@/components/icons";
import React, { useState, useEffect } from "react";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { useStageBasedStories } from "./hooks/useStories";
import { StoriesCard } from "./cards/StoriesCard";
import { ShareWithClientModal } from "./ShareWithClientModal";
import { ShareIcon } from "@/components/icons";
import { useAppStore } from "@/core/store/useAppStore";
import {
  STORIES_STAGE_CONFIGS,
  StoryStage,
  getStoryStageConfig,
} from "./storiesStageConfig";
import { TabGroup, TabItem, TabGroupConfig } from "@/components/ui/TabGroup";
import { PageLayout, PageHeader, PageSection } from "@/components/PageLayout";

const stageColorMap: Record<StoryStage, string> = {
  draft: "bg-slate-500",
  approval: "bg-amber-500",
  scheduled: "bg-purple-500",
  published: "bg-emerald-500",
  discarded: "bg-gray-500",
};

// Board tabs: the five workflow stages plus "Tutti" (no stage filter).
type StoriesTab = StoryStage | "all";

const StoriesViewContent: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const { selectedClient } = useAppStore();
  const navigate = useNavigate();

  const {
    posts,
    isLoading: postsLoading,
    error: postsError,
    stageCounts,
    activeStage,
    setActiveStage,
    refetch: refetchPosts,
  } = useStageBasedStories();

  // Sync URL params with active stage. "all" (Tutti) maps to a null stage,
  // which useStories treats as unfiltered.
  useEffect(() => {
    const stageParam = searchParams.get("stage") as StoriesTab | null;
    if (!stageParam) return;
    const next = stageParam === "all" ? null : (stageParam as StoryStage);
    if (next !== activeStage) {
      setActiveStage(next);
    }
  }, [searchParams, activeStage, setActiveStage]);

  const handleStageChange = (tab: StoriesTab) => {
    setActiveStage(tab === "all" ? null : tab);
    setSearchParams({ stage: tab });
  };

  const handleRefresh = () => {
    refetchPosts();
  };

  // While the store's selectedClient hasn't caught up to the URL client, the
  // stories hook transiently runs against the previous client. Treat that
  // window as loading, not an error.
  const { clientId: urlClientId } = useParams<{ clientId: string }>();
  const clientResolved = !!selectedClient && selectedClient.id === urlClientId;

  const isInitialLoading =
    !clientResolved || (postsLoading && posts.length === 0);
  const error = clientResolved ? postsError : null;

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <SpinnerIcon className="w-6 h-6 text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="max-w-md mx-auto mt-16">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <div className="w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <ErrorIcon className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">
              Error Loading Stories
            </h3>
            <p className="text-red-600 text-sm mb-4">{error}</p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalCount = Object.values(stageCounts).reduce((a, b) => a + b, 0);

  const stageTabItems: TabItem<StoriesTab>[] = [
    {
      id: "all" as const,
      label: "Tutti",
      badge: totalCount > 0 ? totalCount : undefined,
      badgeVariant: "default" as const,
    },
    ...STORIES_STAGE_CONFIGS.map((stage) => ({
      id: stage.id as StoriesTab,
      label: stage.label,
      icon: (
        <div className={`w-2 h-2 rounded-full ${stageColorMap[stage.id]}`} />
      ),
      badge:
        (stageCounts[stage.id] || 0) > 0 ? stageCounts[stage.id] : undefined,
      badgeVariant: "default" as const,
    })),
  ];

  const stageTabConfig: TabGroupConfig<StoriesTab> = {
    variant: "navigation",
    size: "sm",
    layout: "horizontal",
    hasBackground: true,
  };

  const count = posts.length;
  // null activeStage only occurs when Tutti is selected (the initial state
  // is "draft"), so it maps back to the "all" tab, not to a stage.
  const currentTab: StoriesTab = activeStage ?? "all";

  return (
    <PageLayout>
      <PageHeader>
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex items-center gap-4 lg:gap-6 overflow-x-auto">
            {/* Stage Filter inline */}
            <div className="inline-block">
              <TabGroup
                items={stageTabItems}
                activeId={currentTab}
                onTabChange={handleStageChange}
                config={stageTabConfig}
                isLoading={postsLoading && !posts.length}
                loadingCount={4}
              />
            </div>

            {/* Count Badge */}
            {count > 0 && (
              <div className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium whitespace-nowrap shrink-0">
                {count} {count === 1 ? "storia" : "storie"}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {/* Refresh */}
            <button
              onClick={handleRefresh}
              disabled={postsLoading}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200 disabled:opacity-50"
              title="Refresh"
            >
              <RefreshIcon
                className={`w-5 h-5 ${postsLoading ? "animate-spin" : ""}`}
              />
            </button>

            {/* Share with Client — only on the approval (In attesa) stage */}
            {currentTab === "approval" && selectedClient && (
              <button
                onClick={() => setIsShareModalOpen(true)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all duration-200 font-medium flex items-center gap-2 shadow-xs hover:shadow-md"
                title="Share approval link with client"
              >
                <ShareIcon className="w-4 h-4" />
                Share with Client
              </button>
            )}
          </div>
        </div>
      </PageHeader>

      <PageSection.Canvas>
        {posts.length === 0 ? (
          <div className="text-center py-16">
            {(() => {
              // Tutti empty = no stories exist at all, so it shares the draft
              // stage's icon and auto-create explainer.
              const isAll = currentTab === "all";
              const stageConfig = getStoryStageConfig(
                isAll ? "draft" : currentTab,
              );
              const StageIcon = stageConfig.icon;
              const showExplainer = isAll || currentTab === "draft";
              return (
                <>
                  <div className="w-16 h-16 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                    <StageIcon className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {isAll
                      ? "Nessuna storia"
                      : `Nessuna storia in ${stageConfig.label}`}
                  </h3>
                  <p className="text-gray-600 max-w-md mx-auto mb-6">
                    {showExplainer
                      ? "Le storie vengono create automaticamente quando approvi un video story (9:16) nella Library."
                      : stageConfig.description}
                  </p>
                  {showExplainer && urlClientId && (
                    <div className="flex gap-3 justify-center">
                      <button
                        onClick={() =>
                          navigate(`/app/${urlClientId}/library/stories`)
                        }
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
                      >
                        <LibraryIcon className="w-4 h-4" />
                        Vai alla Library
                      </button>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        ) : (
          <div
            className="py-1"
            style={{
              display: "grid",
              gap: "0.75rem",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            }}
          >
            {posts.map((post) => (
              <StoriesCard
                key={post.id}
                post={post}
                onPostDeleted={refetchPosts}
              />
            ))}
          </div>
        )}
      </PageSection.Canvas>

      {/* Loading indicator for background refreshes */}
      {postsLoading && posts.length > 0 && (
        <div className="fixed bottom-4 right-4 bg-white shadow-xs rounded-full px-3 py-2 border border-gray-100">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <div className="w-3 h-3 bg-blue-500 rounded-full opacity-75"></div>
            <span className="opacity-75">Updating...</span>
          </div>
        </div>
      )}

      {/* Share with Client Modal — generic portal deep link to /client/<id>/posts */}
      {selectedClient && (
        <ShareWithClientModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          clientId={selectedClient.id}
          clientName={selectedClient.clientName}
          redirectPath={`/client/${selectedClient.id}/posts`}
        />
      )}
    </PageLayout>
  );
};

export const StoriesView: React.FC = StoriesViewContent;
